// Dynamic white-label branding.
//
// On load we fetch the public GET /api/branding (keyed off the Host header
// server-side). When the org is served on its own verified custom domain and
// has branding enabled, we apply company name (title), favicon, and accent
// colour — all without hardcoding. On prodify360.co it returns custom:false
// and we leave the shipped defaults untouched.

const API_URL = import.meta.env.VITE_API_URL || '';

type Branding = {
  companyName: string;
  logo: string;
  primaryColor: string;
  favicon: string;
  custom: boolean;
};

// "#3b82f6" → "217 91% 60%" (the space-separated HSL the theme's CSS vars use).
function hexToHslTriplet(hex: string): string | null {
  const m = /^#?([0-9a-fA-F]{6})$/.exec(hex.trim());
  if (!m) return null;
  const int = parseInt(m[1], 16);
  const r = ((int >> 16) & 255) / 255;
  const g = ((int >> 8) & 255) / 255;
  const b = (int & 255) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  const l = (max + min) / 2;
  const d = max - min;
  const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
  if (d !== 0) {
    switch (max) {
      case r: h = ((g - b) / d) % 6; break;
      case g: h = (b - r) / d + 2; break;
      default: h = (r - g) / d + 4;
    }
    h *= 60;
    if (h < 0) h += 360;
  }
  return `${Math.round(h)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

function setFavicon(href: string) {
  if (!href) return;
  let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = href;
}

// Cache the last-applied branding so a fresh paint (after the SPA mounts) can
// re-read it synchronously without a second network round-trip.
let cached: Branding | null = null;
export const getBranding = (): Branding | null => cached;

export async function applyBranding(): Promise<void> {
  try {
    const res = await fetch(`${API_URL}/api/branding`, { credentials: 'include' });
    if (!res.ok) return;
    const b: Branding = await res.json();
    cached = b;
    if (!b || !b.custom) return;

    if (b.companyName) document.title = b.companyName;
    if (b.favicon) setFavicon(b.favicon);

    const triplet = hexToHslTriplet(b.primaryColor || '');
    if (triplet) {
      const root = document.documentElement;
      // Drive the accent across the theme's primary-derived tokens.
      root.style.setProperty('--primary', triplet);
      root.style.setProperty('--ring', triplet);
      root.style.setProperty('--sidebar-primary', triplet);
      root.style.setProperty('--sidebar-ring', triplet);
    }
  } catch {
    /* branding is best-effort — never block app boot */
  }
}
